'use client';

import React from 'react';
import { FeatherCheck } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';

interface StepVerticalProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'completed' | 'active';
  stepNumber?: React.ReactNode;
  label?: React.ReactNode;
  description?: React.ReactNode;
  lastStep?: boolean;
  className?: string;
}

const StepVertical = React.forwardRef<HTMLDivElement, StepVerticalProps>(function StepVertical(
  {
    variant = 'default',
    stepNumber,
    label,
    description,
    lastStep = false,
    className,
    ...otherProps
  }: StepVerticalProps,
  ref,
) {
  return (
    <div
      className={SubframeUtils.twClassNames('group/step-vertical flex w-full gap-3', className)}
      ref={ref}
      {...otherProps}
    >
      <div className="flex flex-col items-center">
        <div
          className={SubframeUtils.twClassNames(
            'flex h-8 w-8 flex-none items-center justify-center rounded-full transition-all',
            {
              'bg-neutral-100': variant === 'default',
              'bg-brand-600': variant === 'active',
              'bg-success-100': variant === 'completed',
            },
          )}
        >
          {variant === 'completed' ? (
            <SubframeCore.IconWrapper className="text-caption font-caption text-success-700">
              <FeatherCheck />
            </SubframeCore.IconWrapper>
          ) : (
            stepNumber && (
              <span
                className={SubframeUtils.twClassNames('text-caption-bold font-caption-bold', {
                  'text-subtext-color': variant === 'default',
                  'text-white': variant === 'active',
                })}
              >
                {stepNumber}
              </span>
            )
          )}
        </div>
        {!lastStep && (
          <div
            className={SubframeUtils.twClassNames(
              'mt-1 h-full min-h-[40px] w-px flex-1 transition-all',
              {
                'bg-neutral-200': variant === 'default',
                'bg-brand-200': variant === 'active',
                'bg-success-200': variant === 'completed',
              },
            )}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 pb-6">
        {label && (
          <span
            className={SubframeUtils.twClassNames('text-body transition-all', {
              'text-subtext-color font-body': variant === 'default',
              'text-default-font font-body-bold': variant === 'active',
              'text-default-font font-body': variant === 'completed',
            })}
          >
            {label}
          </span>
        )}
        {description && (
          <span
            className={SubframeUtils.twClassNames('text-caption font-caption text-subtext-color')}
          >
            {description}
          </span>
        )}
      </div>
    </div>
  );
});

interface StepperVerticalRootProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const StepperVerticalRoot = React.forwardRef<HTMLDivElement, StepperVerticalRootProps>(
  function StepperVerticalRoot(
    { children, className, ...otherProps }: StepperVerticalRootProps,
    ref,
  ) {
    return children ? (
      <div
        className={SubframeUtils.twClassNames('flex w-full flex-col', className)}
        ref={ref}
        {...otherProps}
      >
        {children}
      </div>
    ) : null;
  },
);

export const StepperVertical = Object.assign(StepperVerticalRoot, {
  Step: StepVertical,
});
